<template>
    <v-container fluid class="pa-4">
        <!-- Header -->
        <v-row class="mb-2">
            <v-col cols="12">
                <h1 class="text-h5 font-weight-bold">Coaching Settings</h1>
                <p class="text-body-2 text-grey mt-1">
                    Tune which drivers land on the coaching worklist and when follow-ups are overdue
                </p>
            </v-col>
        </v-row>

        <!-- Company filter (admin) -->
        <v-row v-if="isAdmin" class="mb-2">
            <v-col cols="12" sm="6" md="4">
                <company-select v-model="selectedCompany" :items="companies"
                                label="Company" density="compact" variant="outlined" hide-details
                                @update:model-value="fetchData"/>
            </v-col>
        </v-row>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4"/>

        <v-row v-if="isAdmin && !selectedCompany">
            <v-col cols="12">
                <v-alert type="info" variant="tonal" density="compact">
                    Select a company to view and edit its coaching settings.
                </v-alert>
            </v-col>
        </v-row>

        <v-form v-else ref="formRef" v-model="valid" @submit.prevent="save">
            <v-row>
                <v-col cols="12" md="8" lg="6">
                    <v-card elevation="2" class="pa-4">
                        <h3 class="text-subtitle-1 font-weight-bold mb-1">Score bands</h3>
                        <p class="text-caption text-grey mb-4">
                            Scores run 1-10. Drivers below the needs-coaching score appear on the worklist;
                            those at or above the excellent score are top performers.
                        </p>

                        <v-text-field v-model.number="form.needs" type="number" label="Needs-coaching score"
                                      density="comfortable" variant="outlined" :rules="needsRules"
                                      min="1" max="10" step="0.5" class="mb-2"
                                      hint="Drivers scoring below this need coaching" persistent-hint/>

                        <v-text-field v-model.number="form.excellent" type="number" label="Excellent score"
                                      density="comfortable" variant="outlined" :rules="excellentRules"
                                      min="1" max="10" step="0.5" class="mb-2"
                                      hint="Drivers scoring at or above this are top performers" persistent-hint/>

                        <v-divider class="my-4"/>

                        <h3 class="text-subtitle-1 font-weight-bold mb-1">Follow-up cadence</h3>
                        <p class="text-caption text-grey mb-4">
                            Open coaching sessions older than this are flagged as overdue follow-ups.
                        </p>

                        <v-text-field v-model.number="form.followup_days" type="number" label="Follow-up days"
                                      density="comfortable" variant="outlined" :rules="followupRules"
                                      min="1" max="90" step="1"
                                      hint="Flag open sessions left longer than this many days" persistent-hint/>

                        <div class="d-flex justify-end mt-4">
                            <v-btn color="primary" type="submit" :loading="saving" :disabled="!valid">
                                Save
                            </v-btn>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
</template>

<script setup>
import {ref, reactive, computed, onMounted} from 'vue'
import {send} from '@/composables/apiClient'
import {useAuthStore} from '@/stores/authStore'
import {useSnackbarStore} from '@/stores/snackBarStore'
import CompanySelect from "@/components/globals/company-select.vue";

const authStore = useAuthStore()
const {user} = authStore
const {showSnackbar} = useSnackbarStore()

const isAdmin = computed(() => user?.type === 'admin')
const loading = ref(false)
const saving = ref(false)
const valid = ref(false)
const formRef = ref(null)

const companies = ref([])
const selectedCompany = ref(user?.type !== 'admin' ? user?.company?.id : null)

const form = reactive({needs: 5, excellent: 8, followup_days: 7})

const inRange = (v, lo, hi) => v !== '' && v != null && !isNaN(v) && v >= lo && v <= hi

const needsRules = [
    v => inRange(v, 1, 10) || 'Enter a score from 1 to 10',
    v => Number(v) < Number(form.excellent) || 'Must be below the excellent score',
]
const excellentRules = [
    v => inRange(v, 1, 10) || 'Enter a score from 1 to 10',
    v => Number(v) > Number(form.needs) || 'Must be above the needs-coaching score',
]
const followupRules = [
    v => (inRange(v, 1, 90) && Number.isInteger(Number(v))) || 'Enter a whole number of days from 1 to 90',
]

const fetchData = async () => {
    if (isAdmin.value && !selectedCompany.value) return
    loading.value = true
    const params = {}
    if (selectedCompany.value) params.company = selectedCompany.value
    try {
        const response = await send('get', 'drivers/coaching-settings', params)
        const d = response?.data?.data
        if (d) {
            form.needs = d.needs ?? 5
            form.excellent = d.excellent ?? 8
            form.followup_days = d.followup_days ?? 7
        }
    } catch (e) {
        console.error('Failed to load coaching settings:', e)
        showSnackbar('Coaching settings could not be loaded', 'error')
    } finally {
        loading.value = false
    }
}

const save = async () => {
    const check = await formRef.value?.validate()
    if (check && !check.valid) return
    saving.value = true
    const params = {
        needs: form.needs,
        excellent: form.excellent,
        followup_days: form.followup_days,
    }
    if (selectedCompany.value) params.company = selectedCompany.value
    try {
        const response = await send('post', 'drivers/coaching-settings', params)
        showSnackbar(response?.data?.message || 'Coaching settings updated')
    } catch (e) {
        const msg = e?.response?.data?.message || 'Could not save coaching settings'
        showSnackbar(msg, 'error')
    } finally {
        saving.value = false
    }
}

onMounted(() => {
    fetchData()
    if (isAdmin.value) {
        send('get', 'companies', {list: true}).then(r => {
            companies.value = r?.data?.data ?? []
        }).catch(e => console.warn('Could not load companies filter', e))
    }
})
</script>
